/**
 * Minimal, dependency-free HTML sanitizer for admin-authored rich text
 * (Django CKEditor) rendered via `dangerouslySetInnerHTML`.
 *
 * It is NOT a full replacement for DOMPurify  -  it targets the realistic
 * vectors for the CKEditor-sourced HTML we render: script execution and
 * URL-based JS. If untrusted (non-admin) authors ever feed these fields,
 * switch to DOMPurify.
 *
 * Removes: <script>, <style>, <iframe>, <object>, <embed>, <link>, <meta>,
 * <base>, <form> elements; all `on*` event-handler attributes; and
 * `javascript:` / `data:` URLs on href/src/xlink:href.
 */
const FORBIDDEN_TAGS = new Set([
  "SCRIPT",
  "STYLE",
  "IFRAME",
  "OBJECT",
  "EMBED",
  "LINK",
  "META",
  "BASE",
  "FORM",
]);

const URL_ATTRS = ["href", "src", "xlink:href"];

function isUnsafeUrl(value: string): boolean {
  // Strip control chars / whitespace (0x00-0x20) that can hide the scheme
  // (e.g. "java\tscript:"), then test.
  const normalized = value.replace(/[\u0000-\u0020]+/g, "").toLowerCase();
  return (
    normalized.startsWith("javascript:") || normalized.startsWith("data:")
  );
}

export function sanitizeHtml(dirty: string | null | undefined): string {
  if (!dirty) return "";
  // No DOM available (SSR/tests)  -  fail closed by stripping all tags.
  if (typeof document === "undefined" || typeof DOMParser === "undefined") {
    return dirty.replace(/<[^>]*>/g, "");
  }

  const doc = new DOMParser().parseFromString(dirty, "text/html");

  doc.querySelectorAll("*").forEach((el) => {
    if (FORBIDDEN_TAGS.has(el.tagName)) {
      el.remove();
      return;
    }
    // Snapshot attribute names first  -  removing while iterating mutates the list.
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on")) {
        el.removeAttribute(attr.name);
        continue;
      }
      if (URL_ATTRS.includes(name) && isUnsafeUrl(attr.value)) {
        el.removeAttribute(attr.name);
      }
    }
  });

  return doc.body.innerHTML;
}
